// ProdutoForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  RadioGroup,
  Radio,
  Typography,
  Tabs,
  Tab,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Card,
  CardContent,
  Divider,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../services/axios'; // Ajuste o caminho se necessário

// Exemplo de objeto "vazio" para criação de novo Produto
const novoProdutoDefault = {
  idProduto: 0,
  codigoProduto: '', 
  descricao: '',
  codigoProdutoFabricante: '',
  codigoCEST: '',
  codigoNBM: '',
  codigoAnp: '',
  codigoNfci: '',
  complemento: '',
  eCommerce: true,
  status: true,
  vendaSuspensa: 'N', // (S ou N)
  tipoLista: '1',     // (1,2,3,4,5) – Ajuste ao seu critério
  // Exemplo de sub-objeto
  produtoCusto: {
    valorFrete: 0,
    percentualDesconto: 0,
    valorIpi: 0,
    percentualIpi: 0,
    custoBruto: 0,
    custoLiquido: 0,
    custoMedio: 0,
  },
  produtoPrecificacao: {
    precoVistaVarejo: 0,
    precoPrazoVarejo: 0,
    precoMinimoVarejo: 0,
    precoVistaAtacado: 0,
    precoPrazoAtacado: 0,
    precoMinimoAtacado: 0,
    promocao1: 0,
    promocao2: 0,
  },
  produtoVenda: {
    percentualComissaoAtacado: 0,
    percentualComissaoVarejo: 0,
    // ...
  },
  listaFaixasComissao: [],
  documentos: [],
  pesoBruto: 0,
  pesoLiquido: 0,
  flagEmPe: 'N',
  quantidadeMaxima: 0,
  quantidadeMinima: 0,
  // etc.
};

function ProdutoForm() {
  const { idProduto } = useParams(); // /produto/edit/:idProduto
  const navigate = useNavigate();
  const [produto, setProduto] = useState(novoProdutoDefault);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  // Exemplo de dados auxiliares (fabricantes, tipo item, etc.)
  const [fabricantes, setFabricantes] = useState([]);
  const [tipoItens, setTipoItens] = useState([]);

  const isEdicao = !!idProduto;

  // Carregar dados auxiliares (fabricantes etc.)
  useEffect(() => {
    async function carregarAuxiliares() {
      try {
        const respFab = await axios.get('/api/Fabricantes');
        setFabricantes(respFab.data);

        const respTipo = await axios.get('/api/TipoItens');
        setTipoItens(respTipo.data);
      } catch (error) {
        console.error('Erro ao buscar auxiliares', error);
      }
    }
    carregarAuxiliares();
  }, []);

  // Carrega produto se for edição
  useEffect(() => {
    async function carregarProduto() {
      if (isEdicao) {
        try {
          setLoading(true);
          const { data } = await axios.get(`/api/EstProdutos/${idProduto}`);
          setProduto(data);
        } catch (error) {
          console.error('Erro ao carregar produto:', error);
          alert('Erro ao carregar produto');
        } finally {
          setLoading(false);
        }
      } else {
        // Se for novo, reset
        setProduto(novoProdutoDefault);
      }
    }
    carregarProduto();
  }, [idProduto, isEdicao]);

  // Muda a aba
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Helpers para atualizar campo
  const handleChangeField = (field, value) => {
    setProduto((prev) => ({ ...prev, [field]: value }));
  };

  // Para atualizar campos de sub-objetos (ex: produtoCusto)
  const handleChangeNested = (objName, field, value) => {
    setProduto((prev) => ({
      ...prev,
      [objName]: {
        ...prev[objName],
        [field]: value,
      },
    }));
  };

  // Salvar
  const handleSave = async () => {
    try {
      setLoading(true);
      if (isEdicao) {
        // PUT
        await axios.put(`/api/EstProdutos/${idProduto}`, produto);
        alert('Produto atualizado com sucesso!');
      } else {
        // POST
        await axios.post('/api/EstProdutos', produto);
        alert('Produto criado com sucesso!');
      }
      navigate('/');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar produto');
    } finally {
      setLoading(false);
    }
  };

  // Faixa de Comissão
  const handleAddFaixaComissao = () => {
    const novaFaixa = {
      idFaixaComissao: 0,
      faixaInicial: 0,
      faixaFinal: 0,
      valorFaixaInicial: 0,
      valorFaixaFinal: 0,
      tipoFaixaComissaoEnum: 'T',
    };
    setProduto((prev) => ({
      ...prev,
      listaFaixasComissao: [...prev.listaFaixasComissao, novaFaixa],
    }));
  };

  const handleRemoveFaixaComissao = (index) => {
    const newList = [...produto.listaFaixasComissao];
    newList.splice(index, 1);
    setProduto((prev) => ({ ...prev, listaFaixasComissao: newList }));
  };

  const handleChangeFaixa = (index, field, value) => {
    const newList = [...produto.listaFaixasComissao];
    newList[index] = { ...newList[index], [field]: value };
    setProduto((prev) => ({ ...prev, listaFaixasComissao: newList }));
  };

  // Upload de Documentos (exemplo simples)
  const handleDocumentoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('arquivo', file);
      formData.append('idProduto', produto.idProduto);

      // Envie para o endpoint de upload
      const resp = await axios.post('/api/EstProdutos/uploadDocumento', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const novoDoc = {
        idProdutoDocumento: resp.data.idProdutoDocumento,
        nome: resp.data.nome,
        descricao: resp.data.descricao,
      };

      setProduto((prev) => ({
        ...prev,
        documentos: [...(prev.documentos || []), novoDoc],
      }));
      alert('Documento enviado com sucesso!');
    } catch (err) {
      console.error('Erro ao enviar documento:', err);
      alert('Erro ao enviar documento');
    }
  };

  const handleRemoveDocumento = (idx) => {
    // Se precisar remover do backend, chame DELETE
    const newDocs = [...produto.documentos];
    newDocs.splice(idx, 1);
    setProduto((prev) => ({ ...prev, documentos: newDocs }));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {isEdicao ? 'Editar Produto' : 'Novo Produto'}
          </Typography>
          <Divider />
        </CardContent>
      </Card>

      <Tabs value={tabIndex} onChange={handleTabChange} centered variant="scrollable" scrollButtons="auto">
        <Tab label="Cadastro" />
        <Tab label="Preços" />
        <Tab label="Faixa de Comissão" />
        <Tab label="Parâmetros Fiscais" />
        <Tab label="Documentos" />
        <Tab label="Dados Adicionais" />
      </Tabs>

      {/* -------- TAB 0: CADASTRO -------- */}
{tabIndex === 0 && (
  <Box sx={{ mt: 2 }}>
    {/* Linha 1: Código Produto, Descrição, Apresentação */}
    <Grid container spacing={2}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Código Produto"
          fullWidth
          value={produto.codigoProduto || ''}
          onChange={(e) => handleChangeField('codigoProduto', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={5}>
        <TextField
          label="Descrição"
          fullWidth
          value={produto.descricao || ''}
          onChange={(e) => handleChangeField('descricao', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        {/* Exemplo de "Apresentação": Se for um SELECT com opções do back */}
        <FormControl fullWidth>
          <InputLabel id="apresentacao-label">Apresentação</InputLabel>
          <Select
            labelId="apresentacao-label"
            label="Apresentação"
            value={produto.apresentacao || ''}
            onChange={(e) => handleChangeField('apresentacao', e.target.value)}
          >
            <MenuItem value="">Selecione...</MenuItem>
            <MenuItem value="PT - PACOTE">PT - PACOTE</MenuItem>
            <MenuItem value="CX - CAIXA">CX - CAIXA</MenuItem>
            <MenuItem value="UN - UNIDADE">UN - UNIDADE</MenuItem>
            {/* ...outras */}
          </Select>
        </FormControl>
      </Grid>
    </Grid>

    {/* Linha 2: Cód. Prod. Fabr., Cód. CEST, Cód. NCM, Cód. ANP */}
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Cód. Prod. Fabricante"
          fullWidth
          value={produto.codigoProdutoFabricante || ''}
          onChange={(e) => handleChangeField('codigoProdutoFabricante', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Cód. CEST"
          fullWidth
          value={produto.codigoCEST || ''}
          onChange={(e) => handleChangeField('codigoCEST', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Cód. NCM"
          fullWidth
          value={produto.codigoNBM || ''}
          onChange={(e) => handleChangeField('codigoNBM', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Cód. ANP"
          fullWidth
          value={produto.codigoAnp || ''}
          onChange={(e) => handleChangeField('codigoAnp', e.target.value)}
        />
      </Grid>
    </Grid>

    {/* Linha 3: Complemento, E-commerce?, Ativo? */}
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Complemento"
          multiline
          rows={3}
          fullWidth
          value={produto.complemento || ''}
          onChange={(e) => handleChangeField('complemento', e.target.value)}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <FormControlLabel
          control={
            <Checkbox
              checked={produto.eCommerce || false}
              onChange={(e) => handleChangeField('eCommerce', e.target.checked)}
            />
          }
          label="E-commerce?"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={produto.status || false}
              onChange={(e) => handleChangeField('status', e.target.checked)}
            />
          }
          label="Ativo?"
        />
      </Grid>

      {/* Exemplo de Imagem do Produto */}
      <Grid item xs={6} sm={3}>
        <Typography>Imagem do Produto:</Typography>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            console.log('Imagem selecionada:', e.target.files[0]);
            // Você pode acionar seu upload aqui
          }}
        />
      </Grid>
    </Grid>

    {/* Linha 4: Venda Suspensa, Tipo Preço, Tipo Item */}
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={3}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={produto.vendaSuspensa || 'N'}
            onChange={(e) => handleChangeField('vendaSuspensa', e.target.value)}
          >
            <FormControlLabel value="S" control={<Radio />} label="Venda Suspensa" />
            <FormControlLabel value="N" control={<Radio />} label="Não" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel id="tipo-lista-label">Tipo Preço</InputLabel>
          <Select
            labelId="tipo-lista-label"
            label="Tipo Preço"
            value={produto.tipoLista || '1'}
            onChange={(e) => handleChangeField('tipoLista', e.target.value)}
          >
            <MenuItem value="1">1 - Loja</MenuItem>
            <MenuItem value="2">2 - Atacado</MenuItem>
            <MenuItem value="3">3 - Loja/Atacado Sem Lista</MenuItem>
            <MenuItem value="4">4 - Especial</MenuItem>
            <MenuItem value="5">5 - Não Listado</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={5}>
        <FormControl fullWidth>
          <InputLabel id="tipo-item-label">Tipo Item</InputLabel>
          <Select
            labelId="tipo-item-label"
            label="Tipo Item"
            value={produto.estTipoItem?.idTipoItem || ''}
            onChange={(e) =>
              setProduto((prev) => ({
                ...prev,
                estTipoItem: { idTipoItem: e.target.value },
              }))
            }
          >
            <MenuItem value="">Selecione...</MenuItem>
            {tipoItens.map((ti) => (
              <MenuItem key={ti.idTipoItem} value={ti.idTipoItem}>
                {ti.descricao}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>

    {/* Linha 5: Origem, Código NFCI */}
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="origem-label">Origem</InputLabel>
          <Select
            labelId="origem-label"
            label="Origem"
            value={produto.origem || '0'}
            onChange={(e) => handleChangeField('origem', e.target.value)}
          >
            <MenuItem value="0">0-Nacional, exceto as indicadas nos codigos 3 a 5</MenuItem>
            <MenuItem value="1">
              1-Estrangeira - Importação direta, exceto a indicada no código 6
            </MenuItem>
            <MenuItem value="2">
              2-Estrangeira - Adquirida no mercado interno, exceto a indicada no codigo 7
            </MenuItem>
            <MenuItem value="3">
              3-Nacional, mercadoria ou bem c/ Conteúdo de Importação &gt; 40%
            </MenuItem>
            <MenuItem value="4">
              4-Nacional, c/ processos produtivos básicos (Lei 8.248/91 etc.)
            </MenuItem>
            <MenuItem value="5">
              5-Nacional, mercadoria ou bem c/ Conteúdo de Importação &lt;= 40%
            </MenuItem>
            <MenuItem value="6">
              6-Estrangeira - Importacao direta, sem similar nacional
            </MenuItem>
            <MenuItem value="7">
              7-Estrangeira - Adquirida no mercado interno, sem similar nacional
            </MenuItem>
            <MenuItem value="8">
              8-Nacional, mercadoria ou bem c/ Conteúdo de Importacao &gt; 70%
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Código NFCI"
          fullWidth
          value={produto.codigoNfci || ''}
          onChange={(e) => handleChangeField('codigoNfci', e.target.value)}
        />
      </Grid>
    </Grid>

    {/* Linha 6: Saldo Atual, Saldo Reservado, Saldo Disponível, Saldo Entr Fatura */}
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Saldo Atual"
          fullWidth
          type="number"
          value={produto.saldoAtual || 0}
          onChange={(e) => handleChangeField('saldoAtual', +e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Saldo Reservado"
          fullWidth
          type="number"
          value={produto.saldoReservado || 0}
          onChange={(e) => handleChangeField('saldoReservado', +e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Saldo Disponível"
          fullWidth
          type="number"
          value={produto.saldoDisponivel || 0}
          onChange={(e) => handleChangeField('saldoDisponivel', +e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Saldo Entr Fatura"
          fullWidth
          type="number"
          value={produto.saldoEntrFatura || 0}
          onChange={(e) => handleChangeField('saldoEntrFatura', +e.target.value)}
        />
      </Grid>
    </Grid>

    {/* Linha 7: Fabricante, Produto Principal, SubGrupo, Grupo, Linha de Produto */}
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={4}>
        {/* Se "fabricante" for um Select ou AutoComplete */}
        <FormControl fullWidth>
          <InputLabel>Fabricante</InputLabel>
          <Select
            value={produto.fabricante || ''}
            label="Fabricante"
            onChange={(e) => handleChangeField('fabricante', e.target.value)}
          >
            <MenuItem value="">Selecione...</MenuItem>
            {fabricantes.map((fab) => (
              <MenuItem key={fab.id} value={fab.id}>
                {fab.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={8}>
        {/* Produto Principal (pode ser outro SELECT ou AutoComplete) */}
        <TextField
          label="Prod Principal"
          fullWidth
          value={produto.produtoPrincipal || ''}
          onChange={(e) => handleChangeField('produtoPrincipal', e.target.value)}
        />
      </Grid>
    </Grid>
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel>Grupo</InputLabel>
          <Select
            value={produto.grupo || ''}
            label="Grupo"
            onChange={(e) => handleChangeField('grupo', e.target.value)}
          >
            <MenuItem value="">Selecione...</MenuItem>
            {/* Exemplo de grupos */}
            <MenuItem value="39">39 - ABAIXADOR LÍNGUA</MenuItem>
            <MenuItem value="10">10 - OUTRO GRUPO</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel>Sub Grupo</InputLabel>
          <Select
            value={produto.subGrupo || ''}
            label="Sub Grupo"
            onChange={(e) => handleChangeField('subGrupo', e.target.value)}
          >
            <MenuItem value="">Selecione...</MenuItem>
            {/* Exemplo de subgrupos */}
            <MenuItem value="39">39 - ABAIXADOR LÍNGUA</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel>Linha de Produto</InputLabel>
          <Select
            value={produto.linhaProduto || ''}
            label="Linha de Produto"
            onChange={(e) => handleChangeField('linhaProduto', e.target.value)}
          >
            <MenuItem value="">Selecione...</MenuItem>
            <MenuItem value="1">1 - GIODESC</MenuItem>
            <MenuItem value="2">2 - Outra Linha</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>

    {/* Linha 8: Observação (textarea) */}
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12}>
        <TextField
          label="Observação"
          fullWidth
          multiline
          rows={2}
          value={produto.observacao || ''}
          onChange={(e) => handleChangeField('observacao', e.target.value)}
        />
      </Grid>
    </Grid>

    {/* Linha 9: Cod M.S, Modelo */}
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Cod M.S"
          fullWidth
          value={produto.codigoMs || ''}
          onChange={(e) => handleChangeField('codigoMs', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Modelo"
          fullWidth
          value={produto.modelo || ''}
          onChange={(e) => handleChangeField('modelo', e.target.value)}
        />
      </Grid>
    </Grid>
  </Box>
)}


  {/* -------- TAB 1: PREÇOS -------- */}
{tabIndex === 1 && (
  <Box sx={{ mt: 2 }}>
    {/* Linha superior com info de última compra */}
    <Grid container spacing={2}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Código Produto"
          fullWidth
          disabled
          value={produto.codigoProduto || ''}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Última Compra - Fornecedor"
          fullWidth
          disabled
          // Ajuste o nome da propriedade conforme seu backend
          value={produto.ultimoFornecedorNome || ''}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Data"
          fullWidth
          disabled
          value={produto.dataUltimaCompra || ''}
          // Se for data real, pode converter p/ string formatada
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Quantidade"
          fullWidth
          disabled
          type="number"
          value={produto.quantidadeUltimaCompra || 0}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Valor (R$)"
          fullWidth
          disabled
          type="number"
          value={produto.valorUltimaCompra || 0}
        />
      </Grid>
    </Grid>

    {/* BLOCO: CUSTO */}
    <Typography variant="h6" sx={{ mt: 3 }}>
      CUSTO
    </Typography>
    <Divider sx={{ mb: 2 }} />

    <Grid container spacing={2}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Desconto(%)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.percentualDesconto || 0}
          onChange={(e) =>
            handleChangeNested('produtoCusto', 'percentualDesconto', +e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Acréscimo($)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.valorAcrescimo || 0}
          onChange={(e) =>
            handleChangeNested('produtoCusto', 'valorAcrescimo', +e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Valor Pis/Cofins"
          type="number"
          fullWidth
          value={produto.produtoCusto?.valorPisCofins || 0}
          onChange={(e) =>
            handleChangeNested('produtoCusto', 'valorPisCofins', +e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Custo Médio($)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.custoMedio || 0}
          onChange={(e) => handleChangeNested('produtoCusto', 'custoMedio', +e.target.value)}
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Frete($)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.valorFrete || 0}
          onChange={(e) =>
            handleChangeNested('produtoCusto', 'valorFrete', +e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="IPI($)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.valorIpi || 0}
          onChange={(e) => handleChangeNested('produtoCusto', 'valorIpi', +e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Subst Trib(%)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.percentualSubstituicaoTributaria || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoCusto',
              'percentualSubstituicaoTributaria',
              +e.target.value
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Acréscimo(%):"
          type="number"
          fullWidth
          value={produto.produtoCusto?.outrosAcrecimos || 0}
          onChange={(e) =>
            handleChangeNested('produtoCusto', 'outrosAcrecimos', +e.target.value)
          }
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Cred ICMS(%)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.percentualIcmsCompra || 0}
          onChange={(e) =>
            handleChangeNested('produtoCusto', 'percentualIcmsCompra', +e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Custo Bruto($)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.custoBruto || 0}
          onChange={(e) => handleChangeNested('produtoCusto', 'custoBruto', +e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Custo Líquido($)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.custoLiquido || 0}
          onChange={(e) =>
            handleChangeNested('produtoCusto', 'custoLiquido', +e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Valor Difer ICMS($)"
          type="number"
          fullWidth
          value={produto.produtoVenda?.diferencaIcms || 0}
          onChange={(e) =>
            setProduto((prev) => ({
              ...prev,
              produtoVenda: {
                ...prev.produtoVenda,
                diferencaIcms: +e.target.value,
              },
            }))
          }
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Fator de Entrada(%)"
          type="number"
          fullWidth
          value={produto.fatorEntrada || 0}
          onChange={(e) => handleChangeField('fatorEntrada', +e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Custo Real($)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.custoReal || 0}
          onChange={(e) =>
            handleChangeNested('produtoCusto', 'custoReal', +e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Custo Anterior(%)"
          type="number"
          fullWidth
          value={produto.produtoCusto?.custoRealAnterior || 0}
          onChange={(e) =>
            handleChangeNested('produtoCusto', 'custoRealAnterior', +e.target.value)
          }
        />
      </Grid>
    </Grid>

    {/* BLOCO: PREÇO-VAREJO */}
    <Typography variant="h6" sx={{ mt: 3 }}>
      PREÇO - VAREJO
    </Typography>
    <Divider sx={{ mb: 2 }} />

    <Grid container spacing={2}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Comis Atacado(%)"
          type="number"
          fullWidth
          value={produto.produtoVenda?.percentualComissaoAtacado || 0}
          onChange={(e) =>
            setProduto((prev) => ({
              ...prev,
              produtoVenda: {
                ...prev.produtoVenda,
                percentualComissaoAtacado: +e.target.value,
              },
            }))
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Comis Varejo(%)"
          type="number"
          fullWidth
          value={produto.produtoVenda?.percentualComissaoVarejo || 0}
          onChange={(e) =>
            setProduto((prev) => ({
              ...prev,
              produtoVenda: {
                ...prev.produtoVenda,
                percentualComissaoVarejo: +e.target.value,
              },
            }))
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Financeiro Venda(%)"
          type="number"
          fullWidth
          value={produto.produtoVenda?.financeiroVenda || 0}
          onChange={(e) =>
            setProduto((prev) => ({
              ...prev,
              produtoVenda: {
                ...prev.produtoVenda,
                financeiroVenda: +e.target.value,
              },
            }))
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Prazo Med Venda(d)"
          type="number"
          fullWidth
          value={produto.produtoVenda?.prazoVenda || 0}
          onChange={(e) =>
            setProduto((prev) => ({
              ...prev,
              produtoVenda: {
                ...prev.produtoVenda,
                prazoVenda: +e.target.value,
              },
            }))
          }
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Fator Varejo"
          type="number"
          fullWidth
          value={produto.fatorVarejo || 0}
          onChange={(e) => handleChangeField('fatorVarejo', +e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Marg Varejo(%)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.margemVarejo || 0}
          onChange={(e) =>
            handleChangeNested('produtoPrecificacao', 'margemVarejo', +e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Marg Min Varejo(%)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.margemMinimaVarejo || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'margemMinimaVarejo',
              +e.target.value
            )
          }
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Vista Var($)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.precoVistaVarejo || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'precoVistaVarejo',
              +e.target.value
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Prazo Var($)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.precoPrazoVarejo || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'precoPrazoVarejo',
              +e.target.value
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Min Varejo($)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.precoMinimoVarejo || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'precoMinimoVarejo',
              +e.target.value
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Master Var.($)"
          type="number"
          fullWidth
          value={produto.pcoMinimoVarejoNegociacao || 0}
          onChange={(e) => handleChangeField('pcoMinimoVarejoNegociacao', +e.target.value)}
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Vista ST($)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.precoVistaVarejoSt || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'precoVistaVarejoSt',
              +e.target.value
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Prazo ST($)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.precoPrazoVarejoSt || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'precoPrazoVarejoSt',
              +e.target.value
            )
          }
        />
      </Grid>
    </Grid>

    {/* BLOCO: PREÇO-ATACADO */}
    <Typography variant="h6" sx={{ mt: 3 }}>
      PREÇO - ATACADO
    </Typography>
    <Divider sx={{ mb: 2 }} />

    <Grid container spacing={2}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Fator Atacado"
          type="number"
          fullWidth
          value={produto.fatorAtacado || 0}
          onChange={(e) => handleChangeField('fatorAtacado', +e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Marg Atacado(%)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.margemAtacado || 0}
          onChange={(e) =>
            handleChangeNested('produtoPrecificacao', 'margemAtacado', +e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Marg Min Atacado(%)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.margemMinimaAtacado || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'margemMinimaAtacado',
              +e.target.value
            )
          }
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Vista Atac($)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.precoVistaAtacado || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'precoVistaAtacado',
              +e.target.value
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Prazo Atac($)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.precoPrazoAtacado || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'precoPrazoAtacado',
              +e.target.value
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Min Atacado($)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.precoMinimoAtacado || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'precoMinimoAtacado',
              +e.target.value
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Master Atacado.($)"
          type="number"
          fullWidth
          value={produto.pcoMinimoAtacadoNegociacao || 0}
          onChange={(e) =>
            handleChangeField('pcoMinimoAtacadoNegociacao', +e.target.value)
          }
        />
      </Grid>
    </Grid>

    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Vista ST($)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.precoVistaAtacadoSt || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'precoVistaAtacadoSt',
              +e.target.value
            )
          }
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Prazo ST($)"
          type="number"
          fullWidth
          value={produto.produtoPrecificacao?.precoPrazoAtacadoSt || 0}
          onChange={(e) =>
            handleChangeNested(
              'produtoPrecificacao',
              'precoPrazoAtacadoSt',
              +e.target.value
            )
          }
        />
      </Grid>
    </Grid>

    <Typography variant="h6" sx={{ mt: 3 }}>
      Promoções
    </Typography>
    <Divider sx={{ mb: 2 }} />

    <Grid container spacing={2}>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Promoção 1($)"
          type="number"
          fullWidth
          value={produto.promocao1 || 0}
          onChange={(e) => handleChangeField('promocao1', +e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          label="Preço Promoção 2($)"
          type="number"
          fullWidth
          value={produto.promocao2 || 0}
          onChange={(e) => handleChangeField('promocao2', +e.target.value)}
        />
      </Grid>
    </Grid>

    {/* Botões de ação (exemplo) */}
    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
      <Button variant="outlined" onClick={/* Lógica de re-calcular preços */ () => {}}>
        + Re-calcular preços
      </Button>
      <Button variant="outlined" onClick={/* Lógica de negociação de custo */ () => {}}>
        + Negociação de Custo
      </Button>
    </Box>
  </Box>
)}


      {/* -------- TAB 2: FAIXA DE COMISSÃO -------- */}
      {tabIndex === 2 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">Faixas de Comissão</Typography>
            <Button variant="outlined" onClick={handleAddFaixaComissao}>
              Adicionar
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Faixa Inicial</TableCell>
                <TableCell>Faixa Final</TableCell>
                <TableCell>Vlr Inicial</TableCell>
                <TableCell>Vlr Final</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {produto.listaFaixasComissao?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nenhuma faixa cadastrada
                  </TableCell>
                </TableRow>
              ) : (
                produto.listaFaixasComissao.map((faixa, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        type="number"
                        value={faixa.faixaInicial}
                        onChange={(e) => handleChangeFaixa(idx, 'faixaInicial', +e.target.value)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={faixa.faixaFinal}
                        onChange={(e) => handleChangeFaixa(idx, 'faixaFinal', +e.target.value)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={faixa.valorFaixaInicial}
                        onChange={(e) =>
                          handleChangeFaixa(idx, 'valorFaixaInicial', +e.target.value)
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={faixa.valorFaixaFinal}
                        onChange={(e) =>
                          handleChangeFaixa(idx, 'valorFaixaFinal', +e.target.value)
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={faixa.tipoFaixaComissaoEnum}
                        onChange={(e) =>
                          handleChangeFaixa(idx, 'tipoFaixaComissaoEnum', e.target.value)
                        }
                      >
                        <MenuItem value="T">TODOS</MenuItem>
                        <MenuItem value="A">ATACADO</MenuItem>
                        <MenuItem value="V">VAREJO</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveFaixaComissao(idx)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* -------- TAB 3: PARÂMETROS FISCAIS -------- */}
      {tabIndex === 3 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1">Parâmetros fiscais aqui...</Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Código NFCI"
                fullWidth
                value={produto.codigoNfci || ''}
                onChange={(e) => handleChangeField('codigoNfci', e.target.value)}
              />
            </Grid>
            {/* Exemplo: Checkbox de permitir venda entrega futura */}
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={produto.permitirVendaEntregaFutura ?? true}
                    onChange={(e) =>
                      handleChangeField('permitirVendaEntregaFutura', e.target.checked)
                    }
                  />
                }
                label="Venda de Entrega Futura?"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* -------- TAB 4: DOCUMENTOS -------- */}
      {tabIndex === 4 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6">Documentos</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ mb: 2 }}>
            <Typography>Selecione um Arquivo para Upload:</Typography>
            <input type="file" onChange={handleDocumentoUpload} />
          </Box>

          {/* Tabela de documentos */}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {produto.documentos?.length > 0 ? (
                produto.documentos.map((doc, index) => (
                  <TableRow key={index}>
                    <TableCell>{doc.nome}</TableCell>
                    <TableCell>{doc.descricao}</TableCell>
                    <TableCell align="center">
                      <IconButton color="error" onClick={() => handleRemoveDocumento(index)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    Nenhum documento anexado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* -------- TAB 5: DADOS ADICIONAIS -------- */}
      {tabIndex === 5 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6">Dados Adicionais</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Peso Bruto (Kg)"
                type="number"
                fullWidth
                value={produto.pesoBruto || 0}
                onChange={(e) => handleChangeField('pesoBruto', +e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Peso Líquido (Kg)"
                type="number"
                fullWidth
                value={produto.pesoLiquido || 0}
                onChange={(e) => handleChangeField('pesoLiquido', +e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl component="fieldset" sx={{ mt: 1 }}>
                <RadioGroup
                  row
                  value={produto.flagEmPe || 'N'}
                  onChange={(e) => handleChangeField('flagEmPe', e.target.value)}
                >
                  <FormControlLabel value="S" control={<Radio />} label="Em pé: Sim" />
                  <FormControlLabel value="N" control={<Radio />} label="Não" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Quantidade Máxima"
                type="number"
                fullWidth
                value={produto.quantidadeMaxima || 0}
                onChange={(e) => handleChangeField('quantidadeMaxima', +e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Quantidade Mínima"
                type="number"
                fullWidth
                value={produto.quantidadeMinima || 0}
                onChange={(e) => handleChangeField('quantidadeMinima', +e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Botões no final */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          sx={{ mr: 2 }}
          disabled={loading}
        >
          Salvar
        </Button>
        <Button variant="outlined" onClick={() => navigate('/')} disabled={loading}>
          Cancelar
        </Button>
      </Box>
    </Box>
  );
}

export default ProdutoForm;
